import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faUpload,
  faTrash,
  faSpinner,
  faDownload,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { toast, swalConfirm } from '@/lib/swal';

interface Props {
  inspectionId: string;
  findingId: string;
  canUpload: boolean;
  canDelete: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtBytes(v: string | null | undefined): string {
  if (!v) return '';
  const n = parseInt(v, 10);
  if (isNaN(n)) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentSection({ inspectionId, findingId, canUpload, canDelete }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replacingDocId, setReplacingDocId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['finding-documents', inspectionId, findingId],
    queryFn: () =>
      inspectionService.getAttachments(inspectionId, {
        attachmentType: 'DOCUMENT',
        findingId,
        pageSize: 10,
      }),
  });

  const docs = (data?.data ?? []).filter((a) => a.findingId === findingId);
  const atLimit = docs.length >= 2;

  const uploadMutation = useMutation({
    mutationFn: (fd: FormData) => inspectionService.uploadAttachment(inspectionId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-documents', inspectionId, findingId] });
      // Same reason as VideoSection: readiness is an inspection-wide count.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Document uploaded');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to upload document')),
  });

  const replaceMutation = useMutation({
    mutationFn: ({ attId, fd }: { attId: string; fd: FormData }) =>
      inspectionService.replaceAttachment(inspectionId, attId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-documents', inspectionId, findingId] });
      // Same reason as VideoSection: readiness is an inspection-wide count.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Document replaced');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to replace document')),
    onSettled: () => setReplacingDocId(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (attId: string) => inspectionService.deleteAttachment(inspectionId, attId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-documents', inspectionId, findingId] });
      // Same reason as VideoSection: readiness is an inspection-wide count.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Document removed');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to remove document')),
  });

  const handleFile = (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentType', 'DOCUMENT');
    fd.append('findingId', findingId);
    uploadMutation.mutate(fd);
  };

  const handleReplace = (docId: string, file: File) => {
    setReplacingDocId(docId);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentType', 'DOCUMENT');
    replaceMutation.mutate({ attId: docId, fd });
  };

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Documents</h2>
          <span className="text-xs text-muted-foreground">({docs.length}/2)</span>
        </div>
        {canUpload && !atLimit && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {uploadMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faUpload} className="h-3 w-3" />
            )}
            Upload
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFile(file);
            e.target.value = '';
          }
        }}
      />

      <input
        ref={replaceInputRef}
        type="file"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && replacingDocId) {
            handleReplace(replacingDocId, file);
          }
          e.target.value = '';
        }}
      />

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading…
          </div>
        ) : docs.length === 0 ? (
          canUpload ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex h-16 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faFile} className="h-4 w-4" />
              PDF, DOC or DOCX · max 25 MB
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">No documents attached.</p>
          )
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
              >
                <FontAwesomeIcon icon={faFile} className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {doc.originalFileName}
                  </p>
                  <p className="text-xs text-muted-foreground">{fmtBytes(doc.fileSizeBytes)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {doc.url && (
                    <a
                      href={doc.url}
                      download={doc.originalFileName}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-label="Download document"
                    >
                      <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                    </a>
                  )}
                  {canUpload && (
                    <button
                      onClick={() => {
                        setReplacingDocId(doc.id);
                        replaceInputRef.current?.click();
                      }}
                      disabled={replaceMutation.isPending && replacingDocId === doc.id}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-amber-600 transition hover:bg-amber-500/10"
                      aria-label="Replace document"
                    >
                      {replaceMutation.isPending && replacingDocId === doc.id ? (
                        <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faArrowsRotate} className="h-3 w-3" />
                      )}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => {
                        void swalConfirm({
                          title: 'Remove Document?',
                          text: 'This cannot be undone.',
                          confirmText: 'Remove',
                        }).then((ok) => {
                          if (ok) deleteMutation.mutate(doc.id);
                        });
                      }}
                      disabled={deleteMutation.isPending}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Remove document"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
